import './sparkleBurst.css'

import { useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'
import { Message, Speaker } from './contacts'
import { FadeSlideIn } from './FadeSlideIn'

interface MessageListProps {
  messages: Message[]
  /** Names to put above incoming lines, when more than one voice is in the room. */
  names?: { agent: string; person: string }
  /** Belongs to the last message — answers to pick from, and the like. */
  trailing?: React.ReactNode
  /** The agent's bubble, where the default would vanish into the page behind it. */
  agentBubble?: string
  /** Dissolves whichever edge has messages running under it. */
  fadeTop?: boolean
  /** Holds the thread to this width while the scroller runs the full pane. */
  contentWidth?: number
  /** Brings the newest message in, the way the application chat does. */
  animateLast?: boolean
}

/** A thread. Lines are only attributed when two of them are talking back. */
export function MessageList({
  messages,
  names,
  trailing,
  agentBubble = 'bg-ink-50',
  fadeTop,
  contentWidth,
  animateLast,
}: MessageListProps) {
  const scroller = useRef<HTMLDivElement>(null)
  // An edge is only faded while something is actually running under it: at the
  // top of a short thread there is nothing to dissolve, and a mask there would
  // just dim the first message.
  const [edges, setEdges] = useState({ top: false, bottom: false })

  useEffect(() => {
    const node = scroller.current
    if (!node) return
    const update = () =>
      setEdges({
        top: node.scrollTop > 4,
        bottom: node.scrollHeight - node.scrollTop - node.clientHeight > 4,
      })
    update()
    node.addEventListener('scroll', update, { passive: true })
    const resize = new ResizeObserver(update)
    resize.observe(node)
    return () => {
      node.removeEventListener('scroll', update)
      resize.disconnect()
    }
  }, [messages])

  const mask =
    fadeTop && (edges.top || edges.bottom)
      ? `linear-gradient(to bottom, ${edges.top ? 'transparent 0, #000 32px' : '#000 0'}, ${
          edges.bottom ? '#000 calc(100% - 32px), transparent 100%' : '#000 100%'
        })`
      : undefined
  return (
    // The scroller and the bottom-alignment have to be separate elements:
    // justify-end on the scroller itself pushes overflow past the top edge,
    // where no amount of scrolling can reach it.
    <div
      ref={scroller}
      className="min-h-0 flex-1 overflow-y-auto"
      style={mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined}
    >
      <div
        className="mx-auto flex min-h-full w-full flex-col justify-end gap-2 p-3"
        style={contentWidth ? { maxWidth: contentWidth } : undefined}
      >
        {messages.map((message, index) => {
          const you = message.from === Speaker.YOU
          const speaker = names && !you ? (message.from === Speaker.BLUEY ? names.agent : names.person) : null
          const align = you ? 'self-end' : 'self-start'
          const bubble = (
            <div className="flex flex-col gap-0.5">
              {speaker && <span className="text-ink-400 px-1 text-[9px] leading-none">{speaker}</span>}
              {/* pre-line: some lines carry an address the agent reads back. */}
              <span
                className={cn(
                  'rounded-xl px-3 py-2 text-xs leading-relaxed whitespace-pre-line',
                  you ? 'bg-ink text-white' : cn('text-ink', agentBubble)
                )}
              >
                {message.text}
              </span>
            </div>
          )

          // Only the newest one animates; the rest were already on screen.
          return animateLast && index === messages.length - 1 ? (
            <FadeSlideIn key={index} className={cn('max-w-[85%]', align)}>
              {bubble}
            </FadeSlideIn>
          ) : (
            <span key={index} className={cn('flex max-w-[85%] flex-col', align)}>
              {bubble}
            </span>
          )
        })}
        {trailing && <div className="flex flex-col items-start gap-2 pt-1">{trailing}</div>}
      </div>
    </div>
  )
}
